import { CustomError } from "api/interfaces/commonInterface.interface";
import * as s from "../LoginStyled";
import PrimaryButton from "components/atoms/buttons/PrimaryButton";
import Input from "components/atoms/input/Input";
import { ButtonWrapper } from "components/templates/modal/ModalTemplateStyled";
import { useCallback, useState } from "react";
import { useMutation } from "react-query";
import useApiError from "hook/useApiError";
import { postLocalConnectTest } from "api/recorderAPI";
import { LocalConnectionInfo } from "api/interfaces/recorderInterface.interface";
import SmallPopupDefaultTemplate from "components/templates/modal/small/SmallPopupDefaultTemplate";
import { Text } from "components/atoms/text/Text";
import InfoBubble from "components/blocks/bubble/InfoBubble";
import { FaInfoCircle } from "react-icons/fa";
import DW_Spectrum_logo from "assets/images/dw_spectrum_logo.png";
import DW_Spectrum_logo_black from "assets/images/dw_spectrum_logo_b.png";
import { useAppSelector } from "redux/hooks";
import PasswordInput from "components/atoms/input/PasswordInput";

interface Props {
  accountId: string;
  mergedSystemId: string;
  onClose: () => void;
  onClickSave: (connection: LocalConnectionInfo) => void;
}

export enum UPDATE_TYPE {
  HOST,
  PORT,
  USER,
  PWD,
}

export function SpectrumLogin(props: Props): JSX.Element {
  const { handleErrorNotify, notifySuccess, lang } = useApiError();
  const themeMode = useAppSelector((state) => state.theme.theme);

  const [isTestDone, setIsTestDone] = useState<boolean>(false);
  const [connection, setConnection] = useState<LocalConnectionInfo>({
    host: "",
    port: 7001,
    username: "",
    password: "",
  });

  const [isBubble, setIsBubble] = useState<boolean>(false);

  const mutationConnectionTest = useMutation(postLocalConnectTest, {
    onSuccess: () => {
      setIsTestDone(true);
      notifySuccess(lang.label.recorder.notify.connectionTest.success);
    },
    onError: (err: CustomError) => {
      setIsTestDone(false);
      handleErrorNotify(err, lang.label.recorder.notify.connectionTest.fail);
    },
  });

  const onClickTest = useCallback(() => {
    mutationConnectionTest.mutate({
      payload: connection,
    });
  }, [connection, mutationConnectionTest]);

  const onChangeConnectionInfo = useCallback(
    (type: UPDATE_TYPE, value: string | number) => {
      if (type === UPDATE_TYPE.HOST) {
        setConnection((info) => {
          return { ...info, host: value as string };
        });
      } else if (type === UPDATE_TYPE.PORT) {
        const newValue = value as number;
        if (newValue > 65535) {
          setConnection((info) => {
            return { ...info, port: 65535 as number };
          });
        } else {
          setConnection((info) => {
            return { ...info, port: value as number };
          });
        }
      } else if (type === UPDATE_TYPE.USER) {
        setConnection((info) => {
          return { ...info, username: value as string };
        });
      } else if (type === UPDATE_TYPE.PWD) {
        setConnection((info) => {
          return { ...info, password: value as string };
        });
      } else {
        //nothing
      }
    },
    []
  );

  const preCheckSave = () => {
    if (isTestDone) {
      props.onClickSave(connection);
    } else {
    }
  };

  const buttons = (
    <ButtonWrapper>
      <PrimaryButton
        label="Save"
        onClickBtn={preCheckSave}
        disabled={!isTestDone}
        data-testid="auth-signin-cloud-primary-button-pre-check-save"
      />

      <PrimaryButton
        label="Cancel"
        buttonType="cancel"
        onClickBtn={props.onClose}
        data-testid="auth-signin-cloud-primary-button-close"
      />
    </ButtonWrapper>
  );

  return (
    <SmallPopupDefaultTemplate onModal={props.onClose} button={buttons}>
      <s.SpectrumWrapper>
        <s.SpeectrumLogo>
          <img
            src={
              themeMode === "light" ? DW_Spectrum_logo_black : DW_Spectrum_logo
            }
            alt="dw-spectrum-logo"
          />
        </s.SpeectrumLogo>
        <s.TitleWrapper>
          <Text fontSize={20} bold>
            Connect to Server
          </Text>
          <s.Bubble>
            <button
              className="bubble-parent"
              onMouseEnter={() => setIsBubble(true)}
              onMouseLeave={() => setIsBubble(false)}
            >
              <FaInfoCircle />
              {isBubble && (
                <s.BubbleWrapper>
                  <InfoBubble>
                    This system hasn't been registered with DW Spectrum Cloud.{" "}
                    <br />
                    Please provide your connection details to try accessing
                    locally.
                  </InfoBubble>
                </s.BubbleWrapper>
              )}
            </button>
          </s.Bubble>
        </s.TitleWrapper>
        <div className="row">
          <s.SpectrumInputWrapper>
            <span className="label">Host</span>
            <div className="value">
              <Input
                value={connection.host}
                onChange={(e) => {
                  onChangeConnectionInfo(
                    UPDATE_TYPE.HOST,
                    e.currentTarget.value
                  );
                }}
                data-testid="auth-signin-cloud-input-change-connection-info"
              />
            </div>
          </s.SpectrumInputWrapper>
          <s.SpectrumInputWrapper>
            <span className="label">Port</span>
            <div className="value">
              <Input
                type="number"
                value={connection.port}
                min={1}
                max={65535}
                onChange={(e) => {
                  onChangeConnectionInfo(
                    UPDATE_TYPE.PORT,
                    e.currentTarget.value
                  );
                }}
                data-testid="auth-signin-cloud-input-change-connection-info-2"
              />
            </div>
          </s.SpectrumInputWrapper>
        </div>
        <s.SpectrumInputWrapper>
          <span className="label">Login ID</span>
          <div className="value">
            <Input
              value={connection.username}
              onChange={(e) => {
                onChangeConnectionInfo(UPDATE_TYPE.USER, e.currentTarget.value);
              }}
              data-testid="auth-signin-cloud-input-change-connection-info-3"
            />
          </div>
        </s.SpectrumInputWrapper>
        <s.SpectrumInputWrapper>
          <span className="label">Password</span>
          <div className="value">
            <PasswordInput
              value={connection.password}
              onEnter={onClickTest}
              onChange={(e) => {
                onChangeConnectionInfo(UPDATE_TYPE.PWD, e.currentTarget.value);
              }}
              isValidated={false}
              data-testid="auth-signin-cloud-password-input-change-connection-info"
            />

            <PrimaryButton
              label="Test"
              buttonType="cancel"
              onClickBtn={onClickTest}
              data-testid="auth-signin-cloud-primary-button-click-test"
            />
          </div>
        </s.SpectrumInputWrapper>
      </s.SpectrumWrapper>
    </SmallPopupDefaultTemplate>
  );
}
